import currencySettings from '@prisjakt/ui-currency/src/market-settings';
import markets from '../../../config/markets';

const getRoundedCurrency = (price, { code }) =>
  parseFloat(price.toFixed(currencySettings[code].numberOfDecimals));

const getMainDescriptionTranslationKey = product => {
  if (
    !product.prices ||
    !product.prices.meta ||
    !product.prices.meta.itemsTotal
  ) {
    return 'product-no-shops.description';
  }

  if (!product.brand) {
    return 'product-no-brand.description';
  }

  return 'product.description';
};

const getDescriptionTranslationKey = (section, product) => {
  switch (section) {
    case 'properties':
      return 'product-properties.description';
    case 'reviews':
      return 'product-reviews.description';
    case 'statistics':
      return 'product-price-history.description';
    default:
      return getMainDescriptionTranslationKey(product);
  }
};

const getTitleTranslationKey = section => {
  switch (section) {
    case 'properties':
      return 'product-properties.title';
    case 'reviews':
      return 'product-reviews.title';
    case 'statistics':
      return 'product-price-history.title';
    default:
      return 'product.title';
  }
};

const sectionClassicParam = {
  main: 'p',
  properties: 'e',
  reviews: 'o',
  statistics: 'pu',
};

const formatUrl = (market, section, url, id) => {
  if (
    !section ||
    section === 'main' ||
    (market && market.useHashForProductTabs)
  ) {
    return url;
  }

  if (/\?p=/.test(url)) {
    return url.replace(`?p=${id}`, `?${sectionClassicParam[section]}=${id}`);
  }

  if (/--p/.test(url)) {
    return `${url}/${section}`;
  }

  return url;
};

const getLinkType = section =>
  ({
    main: 'product',
    properties: 'productProperties',
    reviews: 'productReviews',
    statistics: 'productPriceHistory',
  }[section || 'main']);

export default ({ data, section }, { market, t, linkTo }) => {
  if (!data) {
    return {};
  }

  const { product } = data;

  if (!product) {
    return {};
  }

  const meta = {
    name: product.name,
    numberOfShops:
      product.prices && product.prices.meta && product.prices.meta.itemsTotal,
    brand: product.brand && product.brand.name,
    category: product.category ? product.category.name : '',
  };

  const title = t('meta', getTitleTranslationKey(section), meta);

  const description = t(
    'meta',
    getDescriptionTranslationKey(section, product),
    meta
  );

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: product.name,
    description,
  };

  // Add image
  if (product.media.first) {
    structuredData.image = product.media.first;
  }

  // Add brand
  if (product.brand && product.brand.name) {
    structuredData.brand = product.brand.name;
  }

  // Add aggregateRating if product has rating
  if (
    product.userReviewSummary &&
    product.userReviewSummary.rating &&
    product.userReviewSummary.count
  ) {
    structuredData.aggregateRating = {
      '@type': 'AggregateRating',
      url: market.domain + product.pathName,
      ratingValue: parseFloat(
        (product.userReviewSummary.rating / 2).toFixed(1) // Convert rating
      ),
      ratingCount: product.userReviewSummary.count,
      worstRating: 0,
      bestRating: 5,
      name: t('reviews', 'panel.title'),
    };
  }

  // Add price information if any
  if (product.prices && product.prices.nodes && product.prices.nodes.length) {
    const prices = product.prices.nodes.map(price => price.price.exclShipping);
    structuredData.offers = {
      '@type': 'AggregateOffer',
      url: market.domain + product.pathName,
      lowPrice: getRoundedCurrency(Math.min(...prices), market),
      highPrice: getRoundedCurrency(Math.max(...prices), market),
      offerCount: prices.length,
      priceCurrency: market.currency,
    };
  } else if (product.priceSummary && product.priceSummary.regular) {
    structuredData.offers = {
      '@type': 'Offer',
      url: market.domain + product.pathName,
      price: getRoundedCurrency(product.priceSummary.regular, market),
      priceCurrency: market.currency,
    };
  }

  // Add review if any
  if (product.userReviews) {
    // @NOTE: Do not include "reviewBody: item.reviewText," (potential XSS).
    structuredData.review = product.userReviews.nodes.map(item => ({
      '@type': 'Review',
      datePublished: item.updatedAt,
      reviewRating: {
        '@type': 'Rating',
        worstRating: 0,
        bestRating: 5,
        ratingValue: parseFloat((item.rating / 2).toFixed(1)), // Convert rating
      },
      author: {
        '@type': 'Person',
        // @NOTE: This assumes display names are validated (!)
        name: item.user ? item.user.name : t('reviews', 'user.anonymous'),
      },
    }));
  }

  if (product.releaseDate) {
    structuredData.releaseDate = product.releaseDate;
  }

  if (
    product.coreProperties &&
    product.coreProperties.nodes &&
    product.coreProperties.nodes.length
  ) {
    const booleanStrings = {
      true: t('product', 'property.boolean-yes'),
      false: t('product', 'property.boolean-no'),
    };
    structuredData.additionalProperty = product.coreProperties.nodes.map(
      item => ({
        '@type': 'PropertyValue',
        name: item.name,
        value:
          item.type === 'boolean'
            ? booleanStrings[item.prettyTable || 'true']
            : item.prettyTable,
      })
    );
  }

  const openGraph = [
    {
      key: 'title',
      content: t('meta', 'product.og-title', meta),
    },
    { key: 'description', content: description },
    {
      key: 'url',
      content: market.domain + product.pathName,
    },
    { key: 'type', content: 'product' },
    product.priceSummary && {
      key: 'amount',
      content: product.priceSummary.regular || product.priceSummary.alternative,
    },
    {
      key: 'currency',
      content: market.currency,
    },
    product.category && {
      key: 'category',
      content: product.category.name,
    },
    product.media && { key: 'image', content: product.media.first },
  ].filter(Boolean);

  const hrefLang = product.hrefLang
    ? product.hrefLang.map(({ locale, url }) => ({
        locale,
        url: formatUrl(
          Object.values(markets).find(
            m => m.hrefLang && m.hrefLang.indexOf(locale) !== -1
          ),
          section,
          url,
          product.id
        ),
      }))
    : [];

  const canonical =
    market.domain +
    (market.useHashForProductTabs
      ? linkTo('product', product)
      : linkTo(getLinkType(section), product));

  return {
    title,
    description,
    thumbnail: product.media && product.media.first,
    openGraph,
    structuredData,
    breadcrumb: product.category && product.category.path,
    canonical,
    android: product.pathName,
    ios: `/product/${product.id}`, // TODO: Semantic id when app can handle it
    hrefLang,
    botify: {
      page: 'product',
      category: product.category && product.category.id,
      parentCategory:
        product.category &&
        product.category.path &&
        product.category.path[product.category.path.length - 2]
          ? product.category.path[product.category.path.length - 2].id
          : null,
      mainCategory:
        product.category &&
        product.category.path &&
        product.category.path[0] &&
        product.category.path[0].id,
    },
  };
};
